import { Link } from 'react-router-dom';
import {
  Button,
  Card,
  Chip,
  Input,
  ListBox,
  Select,
  Spinner,
  Switch,
  TextField,
} from '@heroui/react';
import { ArrowLeft, RefreshCw, Save } from 'lucide-react';
import { useWorkerEditPage } from './useWorkerEditPage';
import { FieldLabel } from '@/components/molecules/FieldLabel';
import { PageContainer } from '@/components/templates/PageContainer';
import { formatDateTime } from '@/lib/formatDate';

/** Заголовок карточки по шкале типографики HeroUI: h5 — «Card titles» (18px/600). */
function CardHeading({ children }: { children: React.ReactNode }) {
  return <Card.Title className="text-lg font-semibold">{children}</Card.Title>;
}

/** Read-only строка в анкете (даты согласий, последний вход, login). */
function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <FieldLabel>{label}</FieldLabel>
      <p className="rounded-[var(--radius)] bg-surface-secondary px-3 py-2 text-sm text-muted">
        {value}
      </p>
    </div>
  );
}

export function WorkerEditPage() {
  const {
    workerId,
    worker,
    isStaff,
    clients,
    workerFieldDefs,
    form,
    login,
    loginForm,
    isLoading,
    notFound,
    isSaving,
    isSavingLogin,
    setField,
    setWorkerFieldValue,
    setLoginField,
    handleSave,
    handleUpdateLogin,
  } = useWorkerEditPage();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-sm text-muted">
        <Spinner size="sm" />
        Загрузка…
      </div>
    );
  }

  if (notFound) {
    return <p className="py-20 text-center text-sm text-muted">Исполнитель не найден</p>;
  }

  // заголовок: ФИО из анкеты (full_name), фолбэк — login
  const title = worker?.name || login?.login;

  return (
    <PageContainer className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Link
            to="/workers"
            className="flex shrink-0 items-center gap-1 text-sm text-muted hover:text-foreground"
          >
            <ArrowLeft className="size-4" />
            Исполнители
          </Link>
          <h2 className="truncate text-2xl font-bold text-foreground">
            {workerId ? title || 'Анкета исполнителя' : 'Новый исполнитель'}
          </h2>
          {worker && (
            <Chip color={worker.enabled ? 'success' : 'danger'} size="sm" variant="soft">
              {worker.enabled ? 'Активен' : 'Неактивен'}
            </Chip>
          )}
        </div>
        <Button onPress={() => void handleSave()} isDisabled={isSaving}>
          {isSaving ? <Spinner size="sm" /> : <Save className="size-4" />}
          {workerId ? 'Сохранить' : 'Создать'}
        </Button>
      </div>

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[1fr_320px]">
        <div className="flex min-w-0 flex-col gap-4">
          {/* При редактировании у персонала здесь только выбор клиента, при
              создании — пароль (логин генерируется автоматически). Клиенту в
              edit-режиме карточка не нужна: его организация подставляется API */}
          {(!workerId || isStaff) && (
            <Card className="bg-surface">
              <Card.Header>
                <CardHeading>Основное</CardHeading>
              </Card.Header>
              <Card.Content className="flex flex-col gap-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {!workerId && (
                    <TextField
                      variant="secondary"
                      value={form.password}
                      onChange={(v) => setField('password', v)}
                      isRequired
                    >
                      <FieldLabel>Пароль</FieldLabel>
                      <Input type="password" placeholder="••••••••" autoComplete="new-password" />
                    </TextField>
                  )}

                  {/* Клиента выбирает только персонал; клиенту подставляется его организация */}
                  {isStaff && (
                    <Select
                      className="w-full"
                      variant="secondary"
                      value={form.clientId}
                      onChange={(key) => setField('clientId', String(key ?? ''))}
                    >
                      <FieldLabel>Клиент</FieldLabel>
                      <Select.Trigger>
                        <Select.Value />
                        <Select.Indicator />
                      </Select.Trigger>
                      <Select.Popover>
                        <ListBox>
                          {clients.map((client) => (
                            <ListBox.Item key={client.id} id={client.id} textValue={client.title}>
                              {client.title}
                              <ListBox.ItemIndicator />
                            </ListBox.Item>
                          ))}
                        </ListBox>
                      </Select.Popover>
                    </Select>
                  )}
                </div>
              </Card.Content>
            </Card>
          )}

          {/* Анкета: внутренний ID (телефон, гео и пр. — настраиваемые поля клиента) */}
          <Card className="bg-surface">
            <Card.Header>
              <CardHeading>Анкета</CardHeading>
            </Card.Header>
            <Card.Content className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {workerId && <ReadOnlyField label="ID исполнителя" value={workerId} />}
              <TextField
                variant="secondary"
                value={form.internalId}
                onChange={(v) => setField('internalId', v)}
              >
                <FieldLabel>Внутренний ID</FieldLabel>
                <Input placeholder="w-001" />
              </TextField>
            </Card.Content>
          </Card>

          <Card className="bg-surface">
            <Card.Header>
              <CardHeading>Данные сотрудника</CardHeading>
            </Card.Header>
            <Card.Content className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {workerFieldDefs.length === 0 ? (
                <p className="text-sm text-muted sm:col-span-2">
                  {workerId
                    ? 'Организация не включила поля сотрудника'
                    : 'Выберите клиента, чтобы увидеть поля сотрудника'}
                </p>
              ) : (
                workerFieldDefs.map((def) => (
                  <TextField
                    variant="secondary"
                    key={def.slug}
                    value={form.workerFields[def.slug] ?? ''}
                    onChange={(v) => setWorkerFieldValue(def.slug, v)}
                  >
                    <FieldLabel>
                      {def.label}
                      {def.status === 'required' && <span className="text-danger"> *</span>}
                    </FieldLabel>
                    <Input type={def.slug === 'email' ? 'email' : 'text'} />
                  </TextField>
                ))
              )}
            </Card.Content>
          </Card>
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          {/* Логин — отдельный вызов бэкенда, своя кнопка «Обновить». Login read-only. */}
          {workerId && login && (
            <Card className="bg-surface">
              <Card.Header>
                <CardHeading>Логин</CardHeading>
              </Card.Header>
              <Card.Content className="flex flex-col gap-4">
                <ReadOnlyField label="Логин" value={login.login} />
                <TextField
                  variant="secondary"
                  value={loginForm.password}
                  onChange={(v) => setLoginField('password', v)}
                >
                  <FieldLabel>Новый пароль</FieldLabel>
                  <Input type="password" placeholder="••••••••" autoComplete="new-password" />
                </TextField>
                <Switch
                  isSelected={loginForm.enabled}
                  isDisabled={isSavingLogin}
                  onChange={(selected) => setLoginField('enabled', selected)}
                >
                  <Switch.Content>
                    <Switch.Control>
                      <Switch.Thumb />
                    </Switch.Control>
                    Активен
                  </Switch.Content>
                </Switch>
                <ReadOnlyField label="Последний вход" value={formatDateTime(login.lastLoginAt)} />
                <Button
                  size="sm"
                  variant="outline"
                  className="self-start"
                  onPress={() => void handleUpdateLogin()}
                  isDisabled={isSavingLogin}
                >
                  {isSavingLogin ? <Spinner size="sm" /> : <RefreshCw className="size-4" />}
                  Обновить
                </Button>
              </Card.Content>
            </Card>
          )}

          {/* Даты согласий проставляются из мобильного приложения — здесь read-only */}
          <Card className="bg-surface">
            <Card.Header>
              <CardHeading>Согласия</CardHeading>
            </Card.Header>
            <Card.Content className="flex flex-col gap-3">
              <ReadOnlyField
                label="Согласие на обработку ПД"
                value={formatDateTime(worker?.pdConsentAcceptedAt)}
              />
              <ReadOnlyField label="Оферта" value={formatDateTime(worker?.offerAcceptedAt)} />
              <ReadOnlyField
                label="Деактивирован"
                value={worker?.deactivatedAt ? formatDateTime(worker.deactivatedAt) : '—'}
              />
            </Card.Content>
          </Card>
        </div>
      </div>
    </PageContainer>
  );
}
